type SearchParamsBuilder = {
  setParam: (key: string, value: string | number | boolean) => SearchParamsBuilder;
  setParams: (params: Record<string, string | number | boolean>) => SearchParamsBuilder;
  appendParam: (key: string, value: string | number | boolean) => SearchParamsBuilder;
  removeParam: (key: string) => SearchParamsBuilder;
  clearParams: () => SearchParamsBuilder;
  toString: () => string;
};

/**
 * Creates a chainable search params builder for manipulating URL query strings
 * @param baseUrl - The URL to build upon (string or URL object)
 * @returns A chainable SearchParamsBuilder interface
 * @throws {TypeError} If the baseUrl is invalid
 *
 * @example
 * ```typescript
 * // Single parameter
 * const url1 = searchParamsBuilder('example.com/search')
 *   .setParam('query', 'hello')
 *   .toString();
 * // Returns: 'https://example.com/search?query=hello'
 *
 * // Multiple parameters
 * const url2 = searchParamsBuilder('example.com/search')
 *   .setParams({ query: 'hello', page: 1, active: true })
 *   .toString();
 * // Returns: 'https://example.com/search?query=hello&page=1&active=true'
 *
 * // Append multiple values for same key
 * const url3 = searchParamsBuilder('example.com')
 *   .appendParam('tag', 'js')
 *   .appendParam('tag', 'react')
 *   .toString();
 * // Returns: 'https://example.com/?tag=js&tag=react'
 *
 * // Clear all params
 * const url4 = searchParamsBuilder('example.com?existing=param')
 *   .clearParams()
 *   .setParam('new', 'value')
 *   .toString();
 * // Returns: 'https://example.com/?new=value'
 * ```
 */
const searchParamsBuilder = (baseUrl: string | URL): SearchParamsBuilder => {
  let url: URL;

  try {
    if (baseUrl instanceof URL) {
      url = baseUrl;
    } else {
      const urlString = baseUrl.startsWith('http://') || baseUrl.startsWith('https://') ? baseUrl : `https://${baseUrl}`;
      url = new URL(urlString);
    }
  } catch (error) {
    throw new TypeError(`Invalid URL provided: ${baseUrl}. ${error instanceof Error ? error.message : ''}`, { cause: error });
  }

  const builder: SearchParamsBuilder = {
    setParam: (key: string, value: string | number | boolean) => {
      url.searchParams.set(key, String(value));
      return builder;
    },

    setParams: (params: Record<string, string | number | boolean>) => {
      for (const [key, value] of Object.entries(params)) {
        url.searchParams.set(key, String(value));
      }
      return builder;
    },

    appendParam: (key: string, value: string | number | boolean) => {
      url.searchParams.append(key, String(value));
      return builder;
    },

    removeParam: (key: string) => {
      url.searchParams.delete(key);
      return builder;
    },

    clearParams: () => {
      const keys = Array.from(url.searchParams.keys());
      for (const key of keys) {
        url.searchParams.delete(key);
      }
      return builder;
    },

    toString: () => {
      return url.toString();
    },
  };

  return builder;
};

export { searchParamsBuilder };
